import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import { useUnit } from 'effector-react';
import { $user } from 'models/user';
import Login from '@/pages/Login';
import NotFound from '@/pages/NotFound/NotFound';
import { UserApplicationsPage } from './pages/UserApplications/UserApplicationsPage';
import { UserGroup } from './types/groups';
import { MainLayout } from './components/UI/Layouts/Main/MainLayout';
import { AdminLayout } from './components/UI/Layouts/Admin/AdminLayout';
import { ROUTES } from './config/routes';
import { ApplicationsPage } from './pages/Admin/Applications';
import { UserGroupsPage } from './pages/Admin/UserGroups';
import { ApplicationLinksPage } from './pages/Admin/ApplicationLinks';
import { ApplicationPage } from './pages/ApplicationPage/ApplicationPage';
import { ApplicationGroupsPage } from './pages/Admin/ApplicationGroups';
import { AdminEdit } from './pages/Admin/AdminEdit';
import { AdminCreate } from './pages/Admin/AdminCreate';
import AdminNotFound from './pages/Admin/AdminNotFound';
import { ChatsPage } from './pages/Chats/ChatsPage';
import ChatsContent from './pages/Chats/components/ChatsContent/ChatsContent';
import ChatsAdminPage from './pages/Admin/Chats/Index';
import ChatsTemplatesPage from './pages/Admin/ChatsTemplates/Index';
import { ChatsGroupsPage } from './pages/Admin/ChatsGroups';
import { SendMessagePage } from './pages/Admin/SendMessage';
import NotAllowed from './pages/NotAllowed/AdminNotAllowed';
import { PushNotificationsBridge } from './pwa/PushNotificationsBridge';

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const user = useUnit($user);
  const { keycloak } = useKeycloak();
  if (!keycloak.authenticated || !user) {
    return <Navigate to={'/login?ref=' + encodeURIComponent(window.location.pathname)} replace />;
  }
  return <>{children}</>;
};

const GroupProtectRoute = ({
  children,
  groups,
}: {
  groups?: string[];
  children: React.ReactNode;
}) => {
  const user = useUnit($user);
  const { keycloak } = useKeycloak();

  if (
    !keycloak.authenticated ||
    !user ||
    (groups?.length && !user.groups?.some((userGroup) => groups.includes(userGroup)))
  ) {
    return <Navigate to={ROUTES.NOT_ALLOWED} />;
  }
  return <>{children}</>;
};

const AppRouter = () => {
  const { initialized } = useKeycloak();

  if (!initialized) {
    return <div>Загрузка...</div>;
  }

  return (
    <>
      <PushNotificationsBridge />
      <Routes>
        <Route
          path={ROUTES.HOME}
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route path={ROUTES.HOME} element={<UserApplicationsPage />} />
          <Route path={ROUTES.APPLICATION.BY_ID(':id')} element={<ApplicationPage />} />
          <Route
            path={ROUTES.CHATS.BASE}
            element={
              <ProtectedRoute>
                <GroupProtectRoute groups={[UserGroup.Notifications]}>
                  <ChatsPage />
                </GroupProtectRoute>
              </ProtectedRoute>
            }
          >
            <Route path={ROUTES.CHATS.BY_ID(':chatId')} element={<ChatsContent />} />
          </Route>
        </Route>

        <Route
          path={ROUTES.ADMIN.BASE}
          element={
            <ProtectedRoute>
              <GroupProtectRoute
                groups={[UserGroup.Chats, UserGroup.Applications, UserGroup.UserGroups]}
              >
                <AdminLayout />
              </GroupProtectRoute>
            </ProtectedRoute>
          }
        >
          <Route path={ROUTES.ADMIN.APPLICATION_GROUPS} element={<ApplicationGroupsPage />} />
          <Route path={ROUTES.ADMIN.APPLICATIONS} element={<ApplicationsPage />} />
          <Route path={ROUTES.ADMIN.USER_GROUPS} element={<UserGroupsPage />} />
          <Route path={ROUTES.ADMIN.APPLICATION_LINKS} element={<ApplicationLinksPage />} />

          <Route path={ROUTES.ADMIN.EDIT(':resource', ':id')} element={<AdminEdit />} />
          <Route path={ROUTES.ADMIN.CREATE(':resource')} element={<AdminCreate />} />

          <Route path={ROUTES.ADMIN.CHATS} element={<ChatsAdminPage />} />
          <Route path={ROUTES.ADMIN.CHATS_GROUPS} element={<ChatsGroupsPage />} />
          <Route path={ROUTES.ADMIN.CHATS_TEMPLATES} element={<ChatsTemplatesPage />} />
          <Route path={ROUTES.ADMIN.SEND_MESSAGE} element={<SendMessagePage />} />

          <Route path="*" element={<AdminNotFound />} />
        </Route>

        <Route path={ROUTES.NOT_ALLOWED} element={<NotAllowed />} />

        <Route path={ROUTES.LOGIN} element={<Login />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  );
};

export default AppRouter;
